import React, {useState, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import templateService from "../../../services/template.service";
import Loading from "../../common/Loading";
import Swal from '../../helpers/swal';
import {
    DEFAULT_CALL_INTERACTION_TYPE,
    DEFAULT_CALL_LANGUAGE,
    DEFAULT_CALL_VOICE,
    optionRowsToMap,
} from './callTemplateOptions';
import {
    AdminCallTemplateFields,
    AdminCallTemplateFooter,
    AdminCallTemplateInteractionFields,
    AdminCallTemplateMetaGrid,
    AdminCallTemplatePage,
} from './AdminCallTemplateForm';

const CreateAdminCallTemplate = () => {
    const navigate = useNavigate();
    const [templateTypes, setTemplateTypes] = useState([]);
    const [existingCallTemplates, setExistingCallTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingEcx, setLoadingEcx] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        reminderName: '',
        content: '',
        voice: DEFAULT_CALL_VOICE,
        language: DEFAULT_CALL_LANGUAGE,
        interactionType: DEFAULT_CALL_INTERACTION_TYPE,
        invalidPrompt: '',
        timeoutPrompt: '',
        usedBy: 'CLINIC'
    });
    const [optionRows, setOptionRows] = useState([]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setLoadingEcx(true);

                const [templateTypesResponse, ecxTemplatesResponse] = await Promise.all([
                    templateService.getTemplateTypes(),
                    templateService.getEcxTemplate(0, 1000)
                ]);

                setTemplateTypes(templateTypesResponse.data);
                setExistingCallTemplates(ecxTemplatesResponse.data.content || []);
            } catch (error) {
                console.error('Error fetching data:', error);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to load template data. Please try again.',
                    confirmButtonColor: '#dc3545'
                });
            } finally {
                setLoading(false);
                setLoadingEcx(false);
            }
        };

        fetchData();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleTemplateChange = (selectedReminderName) => {
        const selectedTemplate = templateTypes.find(
            (template) => template.reminderName === selectedReminderName
        );
        setFormData((prev) => ({
            ...prev,
            reminderName: selectedReminderName,
            usedBy: selectedTemplate?.usedBy || prev.usedBy,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const interactionType = formData.interactionType || DEFAULT_CALL_INTERACTION_TYPE;
        if (interactionType === 'DTMF' && optionRows.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: 'Missing options',
                text: 'DTMF templates need at least one digit option.',
                confirmButtonColor: '#0dcaf0',
            });
            return;
        }
        const digits = optionRows.map((row) => row.digit);
        if (interactionType === 'DTMF' && new Set(digits).size !== digits.length) {
            Swal.fire({
                icon: 'warning',
                title: 'Duplicate digits',
                text: 'Each digit option must be unique.',
                confirmButtonColor: '#0dcaf0',
            });
            return;
        }

        try {
            setSubmitting(true);

            const selectedTemplate = templateTypes.find(
                (t) => t.reminderName === formData.reminderName
            );

            if (!selectedTemplate) {
                throw new Error('Selected template not found');
            }

            await templateService.saveEcxTemplate({
                reminderName: formData.reminderName,
                reminderTypeId: selectedTemplate.id,
                mediumType: 'CALL',
                content: formData.content,
                voice: formData.voice,
                language: formData.language,
                interactionType,
                options: interactionType === 'DTMF' ? optionRowsToMap(optionRows) : null,
                invalidPrompt: formData.invalidPrompt || null,
                timeoutPrompt: formData.timeoutPrompt || null,
                usedBy: formData.usedBy,
            });

            await Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: 'Call template created successfully!',
                confirmButtonColor: '#0dcaf0',
                timer: 2000,
                showConfirmButton: true
            });

            navigate('/admin-templates');
        } catch (error) {
            console.error('Error creating Call template:', error);
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.response?.data?.message || 'Failed to create Call template. Please try again.',
                confirmButtonColor: '#dc3545'
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancel = () => {
        Swal.fire({
            title: 'Discard draft?',
            text: 'You will lose unsaved changes.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, discard',
            cancelButtonText: 'Keep editing',
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
        }).then((result) => {
            if (result.isConfirmed) {
                navigate('/admin-templates');
            }
        });
    };

    const availableCallTemplates = templateTypes.filter((template) => {
        if (!template.isCall) return false;
        return !existingCallTemplates.some(
            (existing) =>
                existing.reminderTypeId === template.id &&
                existing.mediumType === 'CALL'
        );
    });

    const createdCallTemplates = templateTypes.filter((template) =>
        existingCallTemplates.some(
            (existing) =>
                existing.reminderTypeId === template.id &&
                existing.mediumType === 'CALL'
        )
    );

    const selectedType = templateTypes.find(
        (t) => t.reminderName === formData.reminderName
    );

    if (loading || loadingEcx) {
        return (
            <AdminCallTemplatePage
                title="Create Call Template"
                subtitle="Master ECX call script installed to clinics"
            >
                <Loading />
            </AdminCallTemplatePage>
        );
    }

    return (
        <AdminCallTemplatePage
            title="Create Call Template"
            subtitle="Master ECX call script installed to clinics — including DTMF defaults"
            headerBadge={
                <span className="badge bg-info-subtle text-info border border-info-subtle">
                    New template
                </span>
            }
        >
            <form onSubmit={handleSubmit}>
                {createdCallTemplates.length > 0 && (
                    <div className="alert alert-light admin-call-template-alert mb-4">
                        <div className="fw-semibold mb-2">
                            <i className="bi bi-check-circle text-success me-2"></i>
                            Already created
                        </div>
                        <div className="d-flex flex-wrap gap-2">
                            {createdCallTemplates.map((template) => (
                                <span key={template.id} className="badge bg-secondary-subtle text-secondary">
                                    {template.reminderName}
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                {availableCallTemplates.length === 0 && (
                    <div className="alert alert-warning admin-call-template-alert mb-4">
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        All available call reminder types already have an ECX template.
                    </div>
                )}

                {selectedType && (
                    <AdminCallTemplateMetaGrid
                        items={[
                            { label: 'Name', value: selectedType.reminderName },
                            { label: 'Category', value: selectedType.rootCategory || '—' },
                            { label: 'Priority', value: selectedType.priority ?? '—' },
                            { label: 'Type ID', value: selectedType.id, mono: true },
                        ]}
                    />
                )}

                <AdminCallTemplateFields
                    formData={formData}
                    onChange={handleChange}
                    usedByDisabled
                    contentField="content"
                    reminderNameControl={
                        <>
                            <select
                                className="form-select"
                                value={formData.reminderName}
                                onChange={(e) => handleTemplateChange(e.target.value)}
                                required
                            >
                                <option value="">Select a template type</option>
                                {availableCallTemplates.map((template) => (
                                    <option key={template.id} value={template.reminderName}>
                                        {template.reminderName}
                                    </option>
                                ))}
                            </select>
                            <div className="form-text">
                                Only reminder types with call enabled and no existing CALL template
                            </div>
                        </>
                    }
                />

                <AdminCallTemplateInteractionFields
                    formData={formData}
                    onChange={handleChange}
                    optionRows={optionRows}
                    onOptionRowsChange={setOptionRows}
                />

                <div className="mt-4 pt-3 border-top">
                    <AdminCallTemplateFooter
                        onCancel={handleCancel}
                        onSubmitLabel="Create call template"
                        submitting={submitting}
                        submitDisabled={!formData.reminderName || availableCallTemplates.length === 0}
                        submitVariant="info"
                    />
                </div>
            </form>
        </AdminCallTemplatePage>
    );
};

export default CreateAdminCallTemplate;
